import { useCallback, useEffect, useMemo, useRef } from "react";
import { useScreenState } from "./useScreenState.js";
import { decodeListFilters, encodeListFilters, listPage, validateListFilters } from "../lib/listState.js";

export function useListFilters(scope, defaults, validate = (v) => v) {
  const normalise = (value) => validate(validateListFilters(value, defaults));
  const [saved, setSaved] = useScreenState(scope, encodeListFilters(defaults),
    (v) => encodeListFilters(normalise(decodeListFilters(v, defaults))));
  const latest = useRef(null);
  latest.current = { defaults, validate: normalise, setSaved };
  const setFilters = useCallback((next) => {
    const { defaults, validate, setSaved } = latest.current;
    setSaved((previous) => {
      const value = validate(decodeListFilters(previous, defaults));
      return encodeListFilters(validate(typeof next === "function" ? next(value) : next));
    });
  }, []);
  const signature = JSON.stringify(decodeListFilters(saved, defaults));
  const value = useMemo(() => JSON.parse(signature), [signature]);
  return [value, setFilters];
}
export function useListPagination(scope, total, filters = null, loading = false, pageSize = 10) {
  const [saved, setSaved] = useScreenState(scope, { page: 1 },
    (v) => ({ page: listPage(v.page, null, pageSize) }));
  const signature = JSON.stringify(filters);
  const previous = useRef({ scope, signature });
  const changed = previous.current.scope === scope && previous.current.signature !== signature;
  const current = changed ? 1 : listPage(saved.page, loading ? null : total, pageSize);
  useEffect(() => {
    previous.current = { scope, signature };
    if ((changed || !loading) && saved.page !== current) setSaved({ page: current });
  }, [scope, signature, loading, saved.page, current, setSaved]);
  return { current, pageSize, showSizeChanger: false,
    onChange: (page) => setSaved({ page: listPage(page, loading ? null : total, pageSize) }) };
}
